/**
 * HelpdeskHomePage — the operator's single starting point for the Digital Employee helpdesk
 * (DIGEMP-HELPDESK-HOME-FRONTEND-071; role-oriented order DIGEMP-FINISH-ROLE-IA-153). The page
 * reads in the technician's priority order: the queue waiting on a human decision leads (oldest
 * wait first), then what each employee is working on right now, then what needs attention and
 * what was recently completed and verified. The counter strip leads with the same
 * human-decision counters.
 *
 * Every panel composes an existing read-only projection (team queue summary, per-employee
 * workload, pending approvals, recent failures, recent runs) and links into the existing
 * employee / run / ticket surfaces instead of duplicating them. Nothing here writes, approves,
 * executes or grants anything — decision routes stay on the run pages this page links to.
 *
 * Truthfulness rules that the panels below must keep: a counter panel that failed to load shows
 * an explicit error, never zeros; an employee whose workload projection could not be read is
 * shown as unknown, never assumed available; waits without a known start time say so.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, agentRunsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { ExperienceTimeline } from "../components/EmployeeWorkspace";

const REFRESH_MS = 15_000;
const FAILURES_REFRESH_MS = 30_000;
const FAILURES_LIMIT = 12;
const OUTCOMES_RUN_LIMIT = 25;

const UNKNOWN_STATUS = { label: "לא ידוע", dot: "bg-gray-300" };

// Same badge vocabulary as the employee workspace and the runs dashboard.
const STATUS_CHIP = {
  available: "bg-green-100 text-green-700",
  busy: "bg-amber-100 text-amber-700",
  away: "bg-gray-200 text-gray-600",
  offline: "bg-gray-200 text-gray-500",
};

const RISK_LABEL = { green: "סיכון נמוך", amber: "סיכון בינוני", red: "סיכון גבוה" };
const RISK_BADGE = {
  green: "bg-green-100 text-green-700 border-green-200",
  amber: "bg-yellow-100 text-yellow-700 border-yellow-200",
  red: "bg-red-100 text-red-700 border-red-200",
};

const QUEUE_STATUS_LABEL = {
  awaiting_technician_review: "ממתין לסקירת טכנאי",
  awaiting_approval: "ממתין לאישור",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return ""; }
}

function waitLabel(iso, now = Date.now()) {
  const started = Date.parse(iso || "");
  if (!Number.isFinite(started)) return "ממתין מזמן שאינו ידוע";
  const minutes = Math.max(0, Math.floor((now - started) / 60_000));
  if (minutes < 60) return `ממתין ${minutes} דק׳`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `ממתין ${hours} שעות`;
  return `ממתין ${Math.floor(hours / 24)} ימים`;
}

// Mirrors the backend's effective_status rule (employee_workspace.effective_status): a disabled
// employee is offline; a stored away/offline self-status is respected; otherwise busy while any
// run is engaged — the workload row's active/review/approval counts are exactly that engaged set
// (review and approval runs included, as _ACTIVE_RUN_STATUSES treats them). A missing workload
// row means the projection is unknown, not that the employee is free.
function availabilityOf(agent, workRow) {
  if (!agent.is_enabled) return { key: "offline", known: true };
  const stored = agent.availability_status || "available";
  if (stored === "away" || stored === "offline") return { key: stored, known: true };
  if (!workRow) return { key: null, known: false };
  const engaged =
    (workRow.active_count || 0) + (workRow.awaiting_review_count || 0) + (workRow.awaiting_approval_count || 0) > 0;
  if (engaged) return { key: "busy", known: true };
  return STATUS_META[stored] ? { key: stored, known: true } : { key: null, known: false };
}

const AVAILABILITY_RANK = { busy: 0, available: 1, away: 2, offline: 3 };

function StatCard({ label, value, hint, to, tone = "text-gray-800" }) {
  const body = (
    <>
      <div className={`text-2xl font-semibold ${tone}`}>{value == null ? "…" : value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
    </>
  );
  const cls = "bg-white rounded-xl border shadow-sm px-3 py-2.5";
  return to ? (
    <Link to={to} aria-label={value == null ? label : `${label}: ${value}`} className={`${cls} hover:border-blue-300 block`}>
      {body}
    </Link>
  ) : (
    <div className={cls} aria-label={value == null ? label : `${label}: ${value}`}>{body}</div>
  );
}

function SectionCard({ title, description, count, children }) {
  return (
    <section aria-label={title} className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {count != null && <span className="text-xs text-gray-400">{count}</span>}
      </div>
      {description && <p className="text-[11px] text-gray-400">{description}</p>}
      {children}
    </section>
  );
}

function LoadError({ message, onRetry }) {
  return (
    <p role="alert" className="text-sm text-red-600">
      {message}{" "}
      <button type="button" onClick={onRetry} className="underline">נסה שוב</button>
    </p>
  );
}

function EmptyLine({ children }) {
  return <p className="text-sm text-gray-400 italic">{children}</p>;
}

function QueueChip({ label, count, to, chip }) {
  const known = Number.isFinite(count);
  const cls = known && count > 0 ? chip : "bg-gray-50 border-gray-200 text-gray-400";
  const text = `${label} ${known ? count : "—"}`;
  const aria = known ? `${label}: ${count}` : `${label}: לא ידוע`;
  return known && count > 0 && to ? (
    <Link to={to} aria-label={aria} className={`text-[11px] px-2 py-0.5 rounded-full border ${cls} hover:underline`}>
      {text}
    </Link>
  ) : (
    <span aria-label={aria} className={`text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>{text}</span>
  );
}

function EmployeeRow({ agent, work }) {
  const avail = availabilityOf(agent, work);
  const meta = avail.known ? STATUS_META[avail.key] : UNKNOWN_STATUS;
  const chipCls = avail.known ? (STATUS_CHIP[avail.key] || STATUS_CHIP.offline) : "bg-gray-100 text-gray-500";
  const current = work ? work.current_run : null;
  const countOf = (field) => (work ? work[field] : null);
  const runsLink = (status) =>
    `/agent-runs?agent_id=${agent.id}${status ? `&status=${status}` : ""}`;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 border-b border-gray-100 last:border-b-0">
      <EmployeeAvatar
        name={agent.name}
        avatarKey={agent.avatar_key}
        avatarColor={agent.avatar_color}
        status={avail.known ? avail.key : undefined}
        showStatus={avail.known}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/agents/${agent.id}`} className="font-medium text-gray-800 hover:text-blue-600 hover:underline">
            {agent.name}
          </Link>
          {agent.role_title && <span className="text-xs text-gray-500">{agent.role_title}</span>}
          <span
            title={avail.known ? meta.label : "נתוני העומס לא נטענו — המצב אינו ידוע"}
            className={`text-[11px] px-1.5 py-0.5 rounded-full ${chipCls}`}
          >
            {meta.label}
          </span>
        </div>
        {current ? (
          <p className="text-xs text-gray-600 truncate">
            עובד כעת על{" "}
            <Link to={`/agent-runs/${current.run_id}`} className="text-blue-600 hover:underline">
              {current.ticket_number ? `קריאה #${current.ticket_number}` : "משימה מתוך שיחה"}
            </Link>
            {current.ticket_title ? ` · ${current.ticket_title}` : ""}
            {current.ticket_id && (
              <>
                {" · "}
                <Link to={`/tickets/${current.ticket_id}?tab=ai-diagnosis`} className="text-blue-600 hover:underline">
                  לכרטיס
                </Link>
              </>
            )}
          </p>
        ) : work ? (
          <p className="text-xs text-gray-400">אין משימה פעילה כרגע</p>
        ) : (
          <p className="text-xs text-gray-400">נתוני העומס של העובד לא נטענו</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <QueueChip label="בעבודה" count={countOf("active_count")} to={runsLink()} chip="bg-violet-50 border-violet-200 text-violet-700" />
        <QueueChip label="ממתין לסקירה" count={countOf("awaiting_review_count")} to={runsLink("pending_review")} chip="bg-amber-50 border-amber-200 text-amber-700" />
        <QueueChip label="ממתין לאישור" count={countOf("awaiting_approval_count")} to={runsLink("awaiting_approval")} chip="bg-orange-50 border-orange-200 text-orange-700" />
        <QueueChip label="כשלים" count={countOf("recent_failure_count")} to={runsLink("failed")} chip="bg-red-50 border-red-200 text-red-700" />
      </div>
    </li>
  );
}

function ApprovalItem({ item }) {
  const action = item.pending_action;
  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{item.agent_name}</span>
        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
          {QUEUE_STATUS_LABEL[item.run_status] || item.run_status}
        </span>
        {item.ticket_id && (
          <Link to={`/tickets/${item.ticket_id}`} className="text-xs text-blue-600 hover:underline">
            {item.ticket_number ? `קריאה #${item.ticket_number}` : "קריאה מקושרת"}
            {item.ticket_title ? ` · ${item.ticket_title}` : ""}
          </Link>
        )}
        {action && (
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full border ${RISK_BADGE[action.risk_level] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
            {RISK_LABEL[action.risk_level] || action.risk_level}
          </span>
        )}
        <span className="text-[11px] text-gray-400 mr-auto whitespace-nowrap">{waitLabel(item.wait_started_at)}</span>
      </div>
      {action && action.description && <p className="text-sm text-gray-700">{action.description}</p>}
      <div className="flex flex-wrap gap-3 text-xs">
        <Link to={`/agent-runs/${item.run_id}`} className="text-blue-600 hover:underline">
          לפתיחת הריצה וההחלטה
        </Link>
        {item.ticket_id && (
          <Link to={`/tickets/${item.ticket_id}?tab=ai-diagnosis`} className="text-blue-600 hover:underline">לכרטיס</Link>
        )}
      </div>
    </li>
  );
}

function FailureItem({ item }) {
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 rounded-lg border border-red-100 bg-red-50/60 p-2.5">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-800">{item.agent_name}</span>
          {item.ticket_id && (
            <Link to={`/tickets/${item.ticket_id}`} className="text-xs text-blue-600 hover:underline">
              {item.ticket_number ? `קריאה #${item.ticket_number}` : "קריאה מקושרת"}
              {item.ticket_title ? ` · ${item.ticket_title}` : ""}
            </Link>
          )}
          <span className="text-[11px] text-gray-400">{fmt(item.failed_at)}</span>
        </div>
        {item.summary && <p className="text-xs text-gray-600 truncate" title={item.summary}>{item.summary}</p>}
      </div>
      <Link to={`/agent-runs/${item.run_id}`} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
        לפתיחת הריצה
      </Link>
    </li>
  );
}

export default function HelpdeskHomePage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const allowed = can("agents_view");

  const teamQ = useQuery({
    queryKey: ["helpdesk-home", "team-queue"],
    queryFn: () => agentRunsApi.teamQueueSummary(),
    enabled: allowed,
    refetchInterval: REFRESH_MS,
  });
  const workloadQ = useQuery({
    queryKey: ["helpdesk-home", "workload"],
    queryFn: () => agentRunsApi.workloadSummary(),
    enabled: allowed,
    refetchInterval: REFRESH_MS,
  });
  const agentsQ = useQuery({
    queryKey: ["helpdesk-home", "agents"],
    queryFn: () => agentsApi.list(),
    enabled: allowed,
    refetchInterval: REFRESH_MS,
  });
  const approvalsQ = useQuery({
    queryKey: ["helpdesk-home", "pending-approvals"],
    queryFn: () => agentRunsApi.pendingApprovals(),
    enabled: allowed,
    refetchInterval: REFRESH_MS,
  });
  const failuresQ = useQuery({
    queryKey: ["helpdesk-home", "recent-failures"],
    queryFn: () => agentRunsApi.recentFailures({ limit: FAILURES_LIMIT }),
    enabled: allowed,
    refetchInterval: FAILURES_REFRESH_MS,
  });
  const runsQ = useQuery({
    queryKey: ["helpdesk-home", "recent-runs"],
    queryFn: () => agentRunsApi.listAll({ limit: OUTCOMES_RUN_LIMIT }),
    enabled: allowed,
    refetchInterval: REFRESH_MS,
  });

  if (permsLoading && !allowed) {
    return <div className="p-6 text-gray-400" dir="rtl">טוען הרשאות…</div>;
  }
  if (!allowed) {
    return <div className="p-6 text-gray-500" dir="rtl">אין הרשאה לצפות בבית העובדים הדיגיטליים</div>;
  }

  const team = teamQ.data;
  const workloadById = new Map((workloadQ.data?.agents ?? []).map((row) => [row.agent_id, row]));
  const employees = (agentsQ.data ?? [])
    .map((agent) => {
      const work = workloadById.get(agent.id) ?? null;
      return { agent, work, avail: availabilityOf(agent, work) };
    })
    .sort((a, b) => {
      const rank = (e) => (e.avail.known ? AVAILABILITY_RANK[e.avail.key] ?? 4 : 4);
      return rank(a) - rank(b) || String(a.agent.name || "").localeCompare(String(b.agent.name || ""), "he");
    });

  const approvals = approvalsQ.data;
  const failures = failuresQ.data;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-gray-800">בית העובדים הדיגיטליים</h1>
        <p className="text-sm text-gray-500">
          נקודת פתיחה אחת לתפעול ההלפדסק, לפי סדר העדיפות: קודם מה שממתין להחלטה אנושית, אחר כך מה
          העובדים עובדים עליו עכשיו, מה דורש תשומת לב, ומה הושלם ואומת לאחרונה.
        </p>
      </header>

      <section aria-label="סיכום הצוות" className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {teamQ.isError ? (
          <div className="col-span-full bg-white rounded-xl border shadow-sm px-3 py-2.5">
            <LoadError message="לא ניתן לטעון את סיכום הצוות" onRetry={() => teamQ.refetch()} />
          </div>
        ) : (
          <>
            {/* Technician-priority order: what waits on a human decision, then attention, then workforce state. */}
            <StatCard
              label="ממתינים לסקירת טכנאי"
              value={team ? team.awaiting_review_count : null}
              to="/agent-runs?status=pending_review"
            />
            <StatCard
              label="ממתינים לאישור"
              value={team ? team.awaiting_approval_count : null}
              to="/agent-runs?status=awaiting_approval"
            />
            <StatCard
              label="כשלים אחרונים"
              value={team ? team.recent_failure_count : null}
              hint={team ? `ב־${team.recent_failure_window_hours} השעות האחרונות` : null}
              to="/agent-runs?status=failed"
            />
            <StatCard label="בעבודה כעת" value={team ? team.active_runs : null} to="/agent-runs" />
            <StatCard label="עובדים" value={team ? team.total_employees : null} to="/agents" />
            <StatCard label="זמינים" value={team ? team.available_count : null} tone="text-green-700" />
            <StatCard label="עסוקים" value={team ? team.busy_count : null} tone="text-amber-700" />
          </>
        )}
      </section>

      <SectionCard
        title="ממתין להחלטה אנושית"
        count={approvals ? `${approvals.pending_count} ריצות · ${approvals.agents_waiting_count} עובדים` : null}
        description="הממתין הוותיק ביותר ראשון — הריצות מושהות עד להחלטה. אישור או דחייה מתבצעים מתוך עמוד הריצה — העמוד הזה מציג בלבד."
      >
        {approvalsQ.isLoading && <EmptyLine>טוען ריצות ממתינות…</EmptyLine>}
        {approvalsQ.isError && <LoadError message="לא ניתן לטעון את הריצות הממתינות" onRetry={() => approvalsQ.refetch()} />}
        {!approvalsQ.isLoading && !approvalsQ.isError && (approvals?.pending ?? []).length === 0 && (
          <EmptyLine>אין ריצות הממתינות להחלטה אנושית</EmptyLine>
        )}
        <ul className="space-y-1.5">
          {(approvals?.pending ?? []).map((item) => (
            <ApprovalItem key={`${item.agent_id}-${item.run_id}`} item={item} />
          ))}
        </ul>
      </SectionCard>

      <SectionCard
        title="העובדים"
        count={agentsQ.data ? `${employees.length} עובדים` : null}
        description="מצב הזמינות מחושב לפי אותם נתוני עומס שהסביבה האישית של העובד מציגה; לחיצה על שם העובד פותחת את סביבת העבודה."
      >
        {agentsQ.isLoading && <EmptyLine>טוען עובדים…</EmptyLine>}
        {agentsQ.isError && <LoadError message="לא ניתן לטעון את רשימת העובדים" onRetry={() => agentsQ.refetch()} />}
        {!agentsQ.isLoading && !agentsQ.isError && employees.length === 0 && (
          <EmptyLine>
            אין עדיין עובדים דיגיטליים.{" "}
            <Link to="/employee-builder" className="text-blue-600 not-italic hover:underline">לבניית עובד ←</Link>
          </EmptyLine>
        )}
        {workloadQ.isError && employees.length > 0 && (
          <p role="alert" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            נתוני העומס לא נטענו — מצב הזמינות של העובדים מוצג כ״לא ידוע״.{" "}
            <button type="button" onClick={() => workloadQ.refetch()} className="underline">נסה שוב</button>
          </p>
        )}
        <ul>
          {employees.map(({ agent, work }) => (
            <EmployeeRow key={agent.id} agent={agent} work={work} />
          ))}
        </ul>
      </SectionCard>

      <SectionCard
        title="דורש תשומת לב — ריצות שנכשלו"
        count={failures ? failures.failure_count : null}
        description={failures ? `ריצות שנכשלו ב־${failures.window_hours} השעות האחרונות` : null}
      >
        {failuresQ.isLoading && <EmptyLine>טוען כשלים אחרונים…</EmptyLine>}
        {failuresQ.isError && <LoadError message="לא ניתן לטעון את הכשלים האחרונים" onRetry={() => failuresQ.refetch()} />}
        {!failuresQ.isLoading && !failuresQ.isError && (failures?.failures ?? []).length === 0 && (
          <EmptyLine>אין ריצות שנכשלו לאחרונה</EmptyLine>
        )}
        <ul className="space-y-1.5">
          {(failures?.failures ?? []).map((item) => (
            <FailureItem key={item.run_id} item={item} />
          ))}
        </ul>
      </SectionCard>

      <section aria-label="תוצאות אחרונות">
        <ExperienceTimeline runs={runsQ.data ?? []} isLoading={runsQ.isLoading} isError={runsQ.isError} showAgent />
      </section>
    </div>
  );
}
